import { useCallback, useContext, useMemo } from "react";
import CheckUpContext from "../../context/check-up";
import { getNotProvidedExams, QUESTION_MAPPING } from "../../utils/check-up";
import AccordionBox from "../accordion-box";
import Icon from "../icon";

const ExamsSummary = () => {
  const { labels, currentRequest } = useContext(CheckUpContext);

  return (
    <div className="box-wrapper__profile">
      <div className="box-wrapper__info">
        {labels?.WELFARE_CHECKUP_EXAMINATION_CAPTION || "Agli esami e visite del pacchetto"}:
      </div>
      <div className="box-wrapper__list app-content__list app-content__text">
        {currentRequest?.packageSelected?.pack?.exams?.map((cat) => (
          <div key={cat.category}>
            <p>
              {labels?.[`WELFARE_CHECKUP_CATEGORY_${cat.category.toUpperCase()}`] || cat.category}
            </p>
            <ul>
              {cat.exams?.map((exam) => (
                <li key={exam.id}>
                  {labels?.[`WELFARE_CHECKUP_EXAM_${exam.name.replace(/ /g, "-")}`] || exam.name}
                </li>
              ))}
            </ul>
          </div>
        ))}
        {currentRequest?.packageSelected?.pack?.maxOptional > 0 &&
          (currentRequest?.packageSelected?.optionals?.length === 0 ? (
            <div>
              <p>
                {labels?.WELFARE_CHECKUP_NO_OPTIONAL_EXAMS_SELECTED ||
                  "Non hai selezionato alcuna visita opzionale"}
              </p>
            </div>
          ) : (
            <div>
              <p>
                {currentRequest?.packageSelected?.pack?.maxOptional ===
                currentRequest?.packageSelected?.optionals?.length
                  ? labels?.WELFARE_CHECKUP_OPTIONAL_EXAMS_CAPTION || "Hai scelto di aggiungere"
                  : (
                      labels?.WELFARE_CHECKUP_OPTIONAL_PARTIAL_SELECTION ||
                      "Hai scelto di aggiungere [N] di [M] esami opzionali"
                    )
                      .replace("[N]", currentRequest?.packageSelected?.optionals?.length)
                      .replace("[M]", currentRequest?.packageSelected?.pack?.maxOptional)}
                :
              </p>
              <ul>
                {currentRequest?.packageSelected?.optionals.map((exam) => (
                  <li key={exam.id}>{exam.name}</li>
                ))}
              </ul>
            </div>
          ))}
      </div>
    </div>
  );
};

const CenterSummary = () => {
  const { labels, currentRequest } = useContext(CheckUpContext);
  const clinic = currentRequest?.clinic;

  const notProvidedExams = getNotProvidedExams(currentRequest, clinic);

  return (
    <div className="box-wrapper__profile">
      {notProvidedExams?.length > 0 && (
        <div className="error-box">
          <Icon name="error" />
          <div
            className="error-box__text"
            dangerouslySetInnerHTML={{
              __html: (
                labels?.WELFARE_CHECKUP_EXAMS_WARNING ||
                "<p><strong>In questo centro non sono previste le visite specialistiche:</strong> <span class=black>[EXAM]</span></p>"
              ).replace(
                "[EXAM]",
                notProvidedExams
                  ?.map((exam) => exam?.name)
                  ?.join(", ")
                  ?.replace(/,([^,]*)$/, ` ${labels?.AND || "and"}$1`)
              ),
            }}
          />
        </div>
      )}
      <div className="box-wrapper__info" data-label={`WELFARE_CHECKUP_BOX_LOCATION`}>
        {labels?.WELFARE_CHECKUP_BOX_LOCATION}:{" "}
        <span>
          {clinic.city ? (
            <span>
              <strong>{clinic.city}</strong> -{" "}
            </span>
          ) : null}
          {clinic.name}
        </span>
      </div>
      {(clinic?.website || clinic?.websiteLabel) && (
        <div className="box-wrapper__info" data-label={`WELFARE_CHECKUP_BOX_WEBSITE`}>
          {labels?.WELFARE_CHECKUP_BOX_WEBSITE}:{" "}
          {clinic?.website ? (
            <a target="_blank" rel="noreferrer" href={clinic?.website}>
              {clinic?.websiteLabel || clinic?.website}
            </a>
          ) : (
            <span>{clinic?.websiteLabel}</span>
          )}
        </div>
      )}
      <div className="box-wrapper__info" data-label={`WELFARE_CHECKUP_BOX_ADDRESS`}>
        {labels?.WELFARE_CHECKUP_BOX_ADDRESS}:{" "}
        <a target="_blank" rel="noreferrer" href={`http://maps.google.com/?q=${clinic?.address}`}>
          {clinic?.address}
        </a>
      </div>
      <div className="box-wrapper__info" data-label={`WELFARE_CHECKUP_BOX_PHONE`}>
        {labels?.WELFARE_CHECKUP_BOX_PHONE}: <span>{clinic.phone}</span>
      </div>
      <div className="box-wrapper__info" data-label={`WELFARE_CHECKUP_BOX_WORKINGHOURS`}>
        {labels?.WELFARE_CHECKUP_BOX_WORKINGHOURS}: <span>{clinic.workingHours}</span>
      </div>
      {clinic.notes && (
        <div className="box-wrapper__info">
          <span>{clinic.notes}</span>
        </div>
      )}
      <div className="error-box">
        <Icon name="error" />
        <div
          className="error-box__text"
          dangerouslySetInnerHTML={{
            __html:
              labels?.WELFARE_CHECKUP_BOOKING_WARNING ||
              "<p>Una volta prenotato il tuo check-up, ricorda che eventuali disdette delle visite concordate dovranno essere comunicate alla clinica con un <strong>anticipo di almeno 24 ore</strong>. In mancanza di comunicazione nel termine indicato il check-up verrà interrotto, salvo gravi e documentati motivi di salute e familiari</p>",
          }}
        />
      </div>
    </div>
  );
};

const CheckUpBoxWrapper = ({ readOnly }) => {
  const { labels, survey, currentRequest, appLang, setCurrentSectionIndex, setPath } =
    useContext(CheckUpContext);
  const questions = Object.entries(QUESTION_MAPPING);

  const data = useMemo(
    () =>
      questions?.map(([key, value]) => {
        const answerValue = value.data(currentRequest, appLang?.value, survey);
        return [
          key,
          {
            value: answerValue,
            behaviour: value.behaviour,
            type: value.answerType,
            box: value.box,
          },
        ];
      }),
    [currentRequest, appLang, questions, survey]
  );

  const onEdit = useCallback(
    (index) => {
      setPath((current) => current.slice(0, index + 1));
      setCurrentSectionIndex(index);
    },
    [setPath, setCurrentSectionIndex]
  );

  const accordionBoxes = useMemo(
    () => [
      {
        title: labels?.WELFARE_CART_BOX_YOUR_DATA || "I tuoi dati",
        editLabel: labels?.WELFARE_BOX_CHANGE_DATA || "Modifica i tuoi dati",
      },
      {
        title: labels?.WELFARE_CHECKUP_EXAMINATIONS_INCLUDED || "Esami e visite comprese",
        editLabel: labels?.WELFARE_CHECKUP_EXAMINATIONS || "Modifica visite",
      },
      {
        title: labels?.WELFARE_CHECKUP_MEDICAL_CENTER || "Centro medico",
        editLabel: labels?.WELFARE_CHECKUP_CHANGE_MEDICAL_CENTER || "Modifica centro medico",
      },
    ],
    [labels]
  );

  return (
    <div className="box-wrapper box-wrapper--accordion">
      {accordionBoxes?.map((accordion, i) => (
        <AccordionBox
          title={accordion.title}
          onEdit={() => onEdit(i)}
          editLabel={accordion.editLabel}
          readOnly={readOnly}
          key={i}
        >
          <span className="box-wrapper__info">
            {data
              ?.filter(([_key, value]) => value.box === i && value.behaviour === "value-only")
              ?.map(([_key, value]) => value.value)
              ?.join(" ")}
          </span>
          {data
            ?.filter(([_key, value]) => value.box === i && value.behaviour === "package")
            ?.map(
              ([key]) =>
                currentRequest?.packageSelected && (
                  <div className="box-wrapper__info" key={key}>
                    <ExamsSummary />
                  </div>
                )
            )}
          {data
            ?.filter(([_key, value]) => value.box === i && value.behaviour === "center")
            ?.map(
              ([key]) =>
                currentRequest?.clinic && (
                  <div className="box-wrapper__info" key={key}>
                    <CenterSummary />
                  </div>
                )
            )}
          {data
            ?.filter(([_key, value]) => value.box === i && !value.behaviour)
            ?.map(
              ([key, value]) =>
                value.value && (
                  <div
                    className="box-wrapper__info"
                    key={key}
                    data-label={`WELFARE_CHECKUP_BOX_${key.toUpperCase()}`}
                  >
                    {labels?.[`WELFARE_CHECKUP_BOX_${key.toUpperCase()}`]}:{" "}
                    <span>{value.value || "-"}</span>
                  </div>
                )
            )}
        </AccordionBox>
      ))}
    </div>
  );
};

export default CheckUpBoxWrapper;
